import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Card,
  CardContent,
  alpha,
  Badge,
  IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  QuestionAnswer as QuestionIcon,
  Chat as ChatIcon,
  ThumbUp as EngagementIcon,
  Visibility as AttentionIcon,
  Refresh as RefreshIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const colors = {
  primary: '#0078D4',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  chart: ['#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438'],
};

// Generate real-time engagement data (last 60 minutes)
const generateEngagementData = () => {
  const now = new Date();
  return Array.from({ length: 60 }, (_, i) => {
    const time = new Date(now.getTime() - (59 - i) * 60000);
    return {
      time: `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`,
      engagement: Math.floor(60 + Math.sin(i / 5) * 25 + Math.random() * 10),
      questions: Math.floor(Math.random() * 5),
      chatActivity: Math.floor(Math.random() * 15),
    };
  });
};

// Mock active participants
const mockParticipants = Array.from({ length: 42 }, (_, i) => ({
  id: i + 1,
  name: `Student ${i + 1}`,
  status: ['active', 'idle', 'inactive'][Math.floor(Math.random() * 10) > 7 ? 2 : Math.floor(Math.random() * 2)],
  engagement: Math.floor(Math.random() * 100),
  questions: Math.floor(Math.random() * 8),
}));

const questionTimeline = [
  { time: '10:05', student: 'Ana M.', question: 'Can you explain the funnel concept again?' },
  { time: '10:12', student: 'Carlos R.', question: 'How does this apply to B2B?' },
  { time: '10:18', student: 'María L.', question: 'What tool do you recommend?' },
  { time: '10:25', student: 'Pedro S.', question: 'Can we see a real example?' },
  { time: '10:31', student: 'Laura F.', question: 'How to measure this in practice?' },
];

const currentTopics = [
  { topic: 'Conversion Funnels', coverage: 85, color: colors.primary },
  { topic: 'Customer Journey', coverage: 60, color: colors.success },
  { topic: 'Analytics Tools', coverage: 40, color: colors.warning },
  { topic: 'Best Practices', coverage: 25, color: colors.purple },
];

const MetricCard = ({ title, value, subtitle, icon: Icon, color, trend }) => {
  return (
    <Card sx={{ height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: alpha(color, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <Icon sx={{ fontSize: 24, color }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 0.5, textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              {value}
            </Typography>
          </Box>
        </Box>
        {subtitle && (
          <Typography variant="body2" sx={{ color: '#666' }}>
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircleIcon sx={{ fontSize: 8, color: trend > 0 ? colors.success : colors.danger }} />
            <Typography variant="caption" sx={{ color: '#666' }}>
              {trend > 0 ? '+' : ''}{trend}% vs last 10 min
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const LiveClassDashboard = () => {
  const [engagementData, setEngagementData] = useState(generateEngagementData());
  const [activeCount, setActiveCount] = useState(38);
  const [questionCount, setQuestionCount] = useState(12);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setEngagementData(prev => {
        const newData = [...prev.slice(1)];
        const lastPoint = prev[prev.length - 1];
        const newTime = new Date();
        newData.push({
          time: `${newTime.getHours().toString().padStart(2, '0')}:${newTime.getMinutes().toString().padStart(2, '0')}`,
          engagement: Math.max(0, Math.min(100, lastPoint.engagement + Math.floor(Math.random() * 10 - 5))),
          questions: Math.floor(Math.random() * 5),
          chatActivity: Math.floor(Math.random() * 15),
        });
        return newData;
      });
      setActiveCount(36 + Math.floor(Math.random() * 5));
      setCurrentTime(new Date());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const activeParticipants = mockParticipants.filter(p => p.status === 'active');
  const engagementRate = ((activeCount / 42) * 100).toFixed(1);

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            🔴 Live Class Monitor
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              label={`Class in progress • ${currentTime.toLocaleTimeString()}`}
              color="error"
              size="small"
              icon={<CircleIcon sx={{ fontSize: 10, animation: 'pulse 2s infinite' }} />}
            />
            <Chip label="Marketing Analytics 101" variant="outlined" size="small" />
            <Chip label="42 enrolled" variant="outlined" size="small" />
          </Box>
        </Box>
        <IconButton onClick={() => setEngagementData(generateEngagementData())}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Now"
            value={activeCount}
            subtitle={`${engagementRate}% attendance`}
            icon={PeopleIcon}
            color={colors.primary}
            trend={+3}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Questions"
            value={questionCount}
            subtitle="Last 30 minutes"
            icon={QuestionIcon}
            color={colors.success}
            trend={+15}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Engagement"
            value={`${Math.floor(engagementData[engagementData.length - 1].engagement)}%`}
            subtitle="Last 5 minutes"
            icon={EngagementIcon}
            color={colors.warning}
            trend={-2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Chat Messages"
            value="47"
            subtitle="Last 15 minutes"
            icon={ChatIcon}
            color={colors.purple}
            trend={+8}
          />
        </Grid>
      </Grid>

      {/* Real-time Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Engagement Over Time (Last 60 Minutes)
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={engagementData}>
                <defs>
                  <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.primary} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={colors.primary} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval={9} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="engagement"
                  stroke={colors.primary}
                  fillOpacity={1}
                  fill="url(#engagementGradient)"
                  name="Engagement %"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Topic Coverage
            </Typography>
            {currentTopics.map((topic, index) => (
              <Box key={index} sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {topic.topic}
                  </Typography>
                  <Typography variant="body2" sx={{ color: topic.color, fontWeight: 600 }}>
                    {topic.coverage}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={topic.coverage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(topic.color, 0.1),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: topic.color,
                      borderRadius: 4,
                    }
                  }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Participant Grid & Questions */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Active Participants ({activeCount}/42)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {mockParticipants.map((participant) => (
                <MuiTooltip
                  key={participant.id}
                  title={`${participant.name} - ${participant.engagement}% engagement - ${participant.questions} questions`}
                  arrow
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <CircleIcon
                        sx={{
                          fontSize: 12,
                          color:
                            participant.status === 'active' ? colors.success :
                            participant.status === 'idle' ? colors.warning :
                            colors.danger
                        }}
                      />
                    }
                  >
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: colors.chart[participant.id % colors.chart.length],
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        opacity: participant.status === 'inactive' ? 0.4 : 1,
                        border: participant.questions > 3 ? `2px solid ${colors.warning}` : 'none',
                      }}
                    >
                      {participant.name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                  </Badge>
                </MuiTooltip>
              ))}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleIcon sx={{ fontSize: 10, color: colors.success }} />
                <Typography variant="caption">Active ({activeParticipants.length})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleIcon sx={{ fontSize: 10, color: colors.warning }} />
                <Typography variant="caption">Idle</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircleIcon sx={{ fontSize: 10, color: colors.danger }} />
                <Typography variant="caption">Inactive</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Questions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {questionTimeline.map((q, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(colors.primary, 0.05),
                    border: `1px solid ${alpha(colors.primary, 0.1)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary }}>
                      {q.student}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {q.time}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#444' }}>
                    {q.question}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </Box>
  );
};

export default LiveClassDashboard;

